import { Skeleton } from "../ui/Skeleton";

const TH = "px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint";

// Widths cycled over the rows so the placeholder reads like real, uneven data rather than a grid of bars.
const NAME = ["w-28", "w-36", "w-24", "w-32", "w-28", "w-40"];
const META = ["w-20", "w-24", "w-16", "w-24", "w-20", "w-28"];
const COMPANY = ["w-36", "w-28", "w-44", "w-32", "w-40", "w-28"];
const CITY = ["w-24", "w-20", "w-28", "w-20", "w-24", "w-16"];
const LINKS = [2, 1, 3, 2, 1, 2];

/**
 * `SignalTable` while its rows load: same card, same header, and each cell drawn at the footprint
 * of what it will hold (36px contact buttons, 20px link icons, the 40px logo), so the table keeps
 * its size when the data arrives.
 */
export default function SignalTableSkeleton({ rows = 10, dateLabel = "Ouverture" }: { rows?: number; dateLabel?: string }) {
    return (
        <div className="bg-surface rounded-2xl border border-border overflow-hidden" aria-busy="true" aria-label="Chargement des établissements">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                    <thead className="bg-surface-sunken border-b border-border">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left w-12">
                                <div className="h-4 w-4 rounded-sm border border-border bg-surface" />
                            </th>
                            <th scope="col" className={TH}>Contact</th>
                            <th scope="col" className={TH}>Coordonées</th>
                            <th scope="col" className={TH}>Liens</th>
                            <th scope="col" className={TH}>Entreprise</th>
                            <th scope="col" className={TH}>{dateLabel}</th>
                            <th scope="col" className={`${TH} !text-center w-24`}>Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-surface divide-y divide-border">
                        {Array.from({ length: rows }, (_, i) => {
                            const v = i % NAME.length;
                            return (
                                <tr key={i}>
                                    <td className="px-6 py-4 whitespace-nowrap align-middle">
                                        <div className="h-4 w-4 rounded-sm border border-border" />
                                    </td>

                                    {/* Contact: name, then « 46 ans - 3 établissements » */}
                                    <td className="px-6 py-4 align-middle">
                                        <Skeleton className={`h-3.5 my-0.5 ${NAME[v]}`} />
                                        <Skeleton className={`h-3 mt-1.5 ${META[v]}`} />
                                    </td>

                                    {/* Coordonnées: landline, mobile, email buttons */}
                                    <td className="px-6 py-4 align-middle whitespace-nowrap">
                                        <div className="flex items-center gap-3">
                                            {[0, 1, 2].map((k) => (
                                                <div key={k} className="flex h-9 w-9 items-center justify-center">
                                                    <Skeleton className="h-5 w-5 rounded-lg" />
                                                </div>
                                            ))}
                                        </div>
                                    </td>

                                    {/* Liens: LinkedIn, website, socials */}
                                    <td className="px-6 py-4 align-middle">
                                        <div className="flex items-center gap-3">
                                            {Array.from({ length: LINKS[v] }, (_, k) => (
                                                <Skeleton key={k} className="h-5 w-5 rounded-full" />
                                            ))}
                                        </div>
                                    </td>

                                    {/* Entreprise: logo, name, city, Google rating */}
                                    <td className="px-6 py-4 align-middle">
                                        <div className="flex items-center gap-3">
                                            <Skeleton className="h-10 w-10 shrink-0 rounded-lg" />
                                            <div className="flex flex-col">
                                                <Skeleton className={`h-3.5 my-0.5 ${COMPANY[v]}`} />
                                                <Skeleton className={`h-3 mt-1 ${CITY[v]}`} />
                                                <Skeleton className="h-3 mt-1 w-20" />
                                            </div>
                                        </div>
                                    </td>

                                    <td className="px-6 py-4 align-middle">
                                        <Skeleton className="h-3.5 w-20" />
                                    </td>

                                    {/* Actions: CRM, list, report */}
                                    <td className="px-6 py-4 align-middle">
                                        <div className="flex items-center justify-center gap-3">
                                            {[0, 1, 2].map((k) => (
                                                <Skeleton key={k} className="h-4 w-4 rounded-full" />
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
